import { useLingui } from "@lingui/react/macro";
import { GripHorizontal } from "lucide-react";
import React, {
  createContext,
  Suspense,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "@/utils/shadcn-ui";

import { Dialog as BaseDialog, DialogContent } from "./base/dialog";
import { useDockedPosition } from "./docked-position";
import { IconButton } from "./icon-button";
import { Spinner } from "./spinner";

export function Dialog({
  children,
  open,
  onOpenChange,
  className,
  width,
  docked,
  attachToParent,
}: {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  /** Desktop width, e.g. `"620px"`. Below `sm` the dialog is full-bleed
      regardless of this. */
  width?: string;
  docked?: boolean;
  /** Docked only: open beside the dialog this one is rendered in, rather than
      at the screen's right edge. */
  attachToParent?: boolean;
}) {
  const parent = useContext(DialogElementContext);
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const dock = useDockedPosition(
    docked ? content : null,
    attachToParent ? parent : null,
  );
  const { position } = dock;
  const guard = useRef<CloseGuard | null>(null);
  // Memoized: a new value would re-render every guarded form on each pointer
  // move while the dialog is dragged.
  const close = useMemo(() => {
    async function closeIfAllowed() {
      if (guard.current && !(await guard.current())) return;
      onOpenChange?.(false);
    }
    // Returns nothing, so a Cancel button does not show itself pending while
    // the guard's question is up.
    return { guard, requestClose: () => void closeIfAllowed() };
  }, [onOpenChange]);

  return (
    <BaseDialog
      open={open}
      onOpenChange={(next) =>
        next ? onOpenChange?.(true) : close.requestClose()
      }
      modal={!docked}
    >
      <DialogContent
        ref={setContent}
        {...(docked ? dock.contentProps : undefined)}
        // Both halves are needed: `w-` sets the width, and `sm:max-w-` lifts
        // DialogContent's own `sm:max-w-lg` (512px), which would otherwise cap
        // anything wider. The value rides in a CSS var rather than an inline
        // `width`, which would outrank the `max-sm:` full-bleed rule.
        className={cn(
          width && "w-(--dialog-width) sm:max-w-(--dialog-width)",
          docked && [
            DOCKED_CLASS,
            position
              ? "sm:top-(--dock-y) sm:left-(--dock-x)"
              : "sm:top-[5dvh] sm:right-4 sm:left-auto",
            dock.dragging && "sm:cursor-grabbing",
          ],
          className,
        )}
        style={
          {
            ...(width ? { "--dialog-width": width } : undefined),
            ...(position
              ? { "--dock-x": `${position.x}px`, "--dock-y": `${position.y}px` }
              : undefined),
          } as React.CSSProperties
        }
        showCloseButton={false}
        onInteractOutside={docked ? (e) => e.preventDefault() : undefined}
      >
        <DialogElementContext.Provider value={content}>
          <CloseGuardContext.Provider value={close}>
            <Suspense
              fallback={
                <div className="flex min-h-32 items-center justify-center">
                  <Spinner />
                </div>
              }
            >
              {children}
              {docked && <DockGrip onKeyDown={dock.onKeyDown} />}
            </Suspense>
          </CloseGuardContext.Provider>
        </DialogElementContext.Provider>
      </DialogContent>
    </BaseDialog>
  );
}

type CloseGuard = () => boolean | Promise<boolean>;

/** The card of the enclosing dialog, for a nested one to attach to. */
const DialogElementContext = createContext<HTMLElement | null>(null);

const CloseGuardContext = createContext<{
  guard: React.RefObject<CloseGuard | null>;
  requestClose: () => void;
} | null>(null);

export function useDialogClose(guard?: CloseGuard): () => void {
  const context = useContext(CloseGuardContext);
  if (!context) throw new Error("useDialogClose must be used inside a Dialog");
  useLayoutEffect(() => {
    if (!guard) return;
    context.guard.current = guard;
    return () => {
      context.guard.current = null;
    };
  });
  return context.requestClose;
}

// Pinned near the top, so a form that changes height leaves the row just
// clicked under the cursor. `transition-none`: DialogContent's `duration-200`
// would otherwise ease left/top, and the card would trail the pointer.
const DOCKED_CLASS =
  "transition-none sm:translate-x-0 sm:translate-y-0 sm:[&_[data-slot=dialog-title]]:cursor-grab sm:[&_[data-slot=dialog-title]]:select-none";

function DockGrip({
  onKeyDown,
}: {
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
}) {
  const { t } = useLingui();
  return (
    <IconButton
      icon={GripHorizontal}
      size={18}
      label={t`Move`}
      tooltip={t`Drag to move, or use the arrow keys`}
      aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight"
      data-dock-handle
      onKeyDown={onKeyDown}
      className="text-muted-foreground hover:text-foreground absolute top-6 right-6 cursor-grab touch-none select-none max-sm:hidden"
    />
  );
}
